import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Popover } from '@buffer-mono/legacy-bufferapp-components'

import { createOrUpdateIdea } from '~publish/pages/Create/helpers'
import { useCreateIdea } from '~publish/components/LegacyIdeaComposer/useCreateIdea'
import { useEditIdea } from '~publish/components/LegacyIdeaComposer/useEditIdea'

import { actions } from '../../reducer'
import ComposerWrapper from '../ComposerWrapper'
import { useAppSelector } from '~publish/legacy/store'
import { useDispatch } from 'react-redux'

interface ComposerPopoverProps {
  shouldResetComposerData?: boolean
}

const ComposerPopover = ({
  shouldResetComposerData = false,
}: ComposerPopoverProps): JSX.Element | null => {
  const dispatch = useDispatch()
  const [showDismissComposerModal, setShowDismissComposerModal] =
    useState(false)

  const orgsLoaded = useAppSelector((state) => state.organizations.loaded)
  const userLoaded = useAppSelector((state) => state.user.loaded)
  const profilesLoaded = useAppSelector((state) => state.profileSidebar.loaded)
  const campaignsLoaded = !useAppSelector(
    (state) => state.campaignsList.isLoading,
  )
  const hasCurrentOrg = !!useAppSelector(
    (state) => state.organizations?.selected,
  )

  const isReduxDataLoading =
    !orgsLoaded ||
    !userLoaded ||
    !profilesLoaded ||
    !campaignsLoaded ||
    !hasCurrentOrg

  const showComposerState = useAppSelector(
    (state) => state.composerPopover.showComposer,
  )

  const showComposer = showComposerState && !isReduxDataLoading

  const { createIdea } = useCreateIdea()
  const { updateIdea } = useEditIdea()

  const idea = useAppSelector((state) => state.composerPopover.idea)

  const onOverlayClick = useCallback((): void => {
    setShowDismissComposerModal(true)
  }, [])

  const handleCancelDismissModal = useCallback((): void => {
    setShowDismissComposerModal(false)
  }, [])

  const handleConfirmDismissComposer = useCallback((): void => {
    setShowDismissComposerModal(false)
    dispatch(actions.hideComposer(true))
  }, [dispatch])

  const handleOnSave = useCallback((): void => {
    dispatch(actions.hideComposer())
    if (idea) {
      createOrUpdateIdea({
        idea,
        updateIdea,
        createIdea,
      })
    }
  }, [createIdea, idea, updateIdea, dispatch])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onOverlayClick()
      }
    }
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onOverlayClick])

  // TODO: This should use the default portals container, but it's not working.
  // We should review all the z-indexes of the composer and move it to a Dialog.
  const portalContainer =
    document.getElementById('composer-container') ?? document.body

  if (showComposer) {
    return createPortal(
      <Popover
        width="100%"
        transparentOverlay={false}
        onOverlayClick={onOverlayClick}
      >
        <ComposerWrapper
          type="composerPopover"
          onSave={handleOnSave}
          shouldResetComposerData={shouldResetComposerData}
          onCancelDismissModal={handleCancelDismissModal}
          onConfirmDismissComposer={handleConfirmDismissComposer}
          showDismissComposerModal={showDismissComposerModal}
          onComposerOverlayClick={onOverlayClick}
        />
      </Popover>,
      portalContainer,
    )
  }

  return null
}

export default ComposerPopover
